import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import tr from '../i18n/tr';
import en from '../i18n/en';

const translations = { tr, en };
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LanguageContext = createContext();

export const LanguageProvider = ({ children, forceLang }) => {
  const [language, setLanguageState] = useState(() => {
    if (forceLang) return forceLang;
    const saved = localStorage.getItem('growthriq_lang');
    return saved || 'tr';
  });
  // CMS overrides: { 'landing.hero.title': '…', … } for the active language
  const [overrides, setOverrides] = useState({});

  useEffect(() => {
    if (forceLang) {
      setLanguageState(forceLang);
    }
  }, [forceLang]);

  // Re-fetch overrides whenever the active language changes
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/content`, { params: { lang: language } })
      .then((res) => {
        if (!cancelled) setOverrides(res.data || {});
      })
      .catch(() => {
        if (!cancelled) setOverrides({});
      });
    return () => {
      cancelled = true;
    };
  }, [language]);

  // Listen for an in-app refresh trigger (admin panel fires this after a save)
  useEffect(() => {
    const handler = () => {
      axios
        .get(`${API_URL}/content`, { params: { lang: language } })
        .then((res) => setOverrides(res.data || {}))
        .catch(() => {});
    };
    window.addEventListener('growthriq:content-updated', handler);
    return () => window.removeEventListener('growthriq:content-updated', handler);
  }, [language]);

  const setLanguage = useCallback((lang) => {
    setLanguageState(lang);
    if (!forceLang) {
      localStorage.setItem('growthriq_lang', lang);
    }
  }, [forceLang]);

  const t = useCallback((key, params) => {
    // Admin override wins; otherwise current-lang dict; otherwise TR fallback; otherwise the key itself
    const dict = translations[language] || translations.tr;
    let text = overrides[key] ?? dict[key] ?? translations.tr[key] ?? key;
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        text = text.replace(`{${k}}`, v);
      });
    }
    return text;
  }, [language, overrides]);

  const value = useMemo(
    () => ({ language, setLanguage, t, overrides }),
    [language, setLanguage, t, overrides],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};
